import { action, computed, observable, extendObservable } from "mobx";
import PhotoEditingJob from "~/models/PhotoEditing/PhotoEditingJob";
import PhotoEditingJobRepository from "~/repository/PhotoEditing/PhotoEditingJobRepository";
import NestStore from "~/stores/NestStore";
import Product from "~/models/Product";
import ProductRepository from "~/repository/ProductRepository";
import JobType from "~/constants/PhotoEditing/JobType";
import ProductIdPerJobType from "~/constants/PhotoEditing/ProductIdPerJobType";
import HdRealImagesPerProduct from "~/constants/PhotoEditing/HdRealImagesPerProduct";

class PhotoEditingJobStore extends NestStore {
  rootStore = null;

  /**
   * @property {Array}
   */
  @observable photoEditingJobs = [];

  /**
   * @property {Object|null}
   */
  @observable photoEditingJob = null;

  /**
   * @property {number|null}
   */
  @observable photoEditingJobId = null;

  /**
   * @property {PhotoEditingJobRepository|null}
   */
  photoEditingJobRepository = null;

  /**
   * @property {Object}
   */
  @observable photoEditingImageProducts = {};

  /**
   * @property {Product|null}
   */
  @observable photoEditingImageProduct = null;

  /**
   * @property {ProductRepository|null}
   */
  productRepository = null;

  constructor(rootStore) {
    super();

    this.rootStore = rootStore;

    this.photoEditingJobRepository = new PhotoEditingJobRepository();
    this.productRepository = new ProductRepository();

    this.setDefaultSearchParams();
  }

  @action
  load() {
    this.isLoading = true;

    return Promise.all([
      this.getOne(),
      this.getPhotoEditingImageProduct(JobType.HD_REAL, ProductIdPerJobType[JobType.HD_REAL]),
      this.getPhotoEditingImageProduct(JobType.BASIC_HDR, ProductIdPerJobType[JobType.BASIC_HDR]),
    ]).finally(() => {
      this.photoEditingImageProduct = this.photoEditingImageProducts[this.photoEditingJob.typeId];
      this.isLoading = false;
    });
  }

  @action
  loadIndex() {
    this.isLoading = true;

    return Promise.all([this.getAll()]).finally(() => {
      this.isLoading = false;
    });
  }

  /**
   * Get all.
   */
  @action
  getAll() {
    const params = {
      offset: this.offset,
      limit: this.pagination.itemsPerPage,
    };

    if (this.searchParams.photoEditingJobIdOrLabel.trim() !== "") {
      params.photoEditingJobIdOrLabel = this.searchParams.photoEditingJobIdOrLabel.trim();
    }

    if (this.searchParams.providerId !== "") {
      params.providerId = this.searchParams.providerId;
    }

    if (this.searchParams.tourId !== "") {
      params.tourId = this.searchParams.tourId;
    }

    if (this.searchParams.status !== "") {
      params.status = this.searchParams.status;
    }

    if (this.searchParams.statuses !== "") {
      params.statuses = this.searchParams.statuses;
    }

    return this.photoEditingJobRepository
      .fetch(params)
      .then(response => {
        this.processEntities(response.data.photoEditingJobs);
        this.processPagination(response.data.pagination);
      })
      .finally(() => {
        // trim the query if it's filled of blank spaces only (aesthetic feature)
        if (this.searchParams.photoEditingJobIdOrLabel.trim() === "") {
          this.searchParams.photoEditingJobIdOrLabel = "";
        }
      });
  }

  @action
  processEntities(photoEditingJobs = []) {
    this.photoEditingJobs = [];

    photoEditingJobs.forEach(photoEditingJobArray => {
      const photoEditingJob = new PhotoEditingJob(photoEditingJobArray);

      this.photoEditingJobs.push(photoEditingJob);
    });
  }

  @action
  getOne() {
    return this.photoEditingJobRepository.fetchOne(this.photoEditingJobId).then(response => {
      this.photoEditingJob = new PhotoEditingJob(response.data.photoEditingJob);
      // decorate images so we can know their 'selected' state.
      for (let i = 0; i < this.photoEditingJob.photoEditingImages.length; i += 1) {
        const photoEditingImage = this.photoEditingJob.photoEditingImages[i];
        // flag as selected those images that are ready
        // flag as selected those images that are ready
        extendObservable(photoEditingImage, {
          isSelected: photoEditingImage.isReady,
        });
      }

      this.photoEditingJob.tickEstimatedTimeLeftToFinish();
    });
  }

  /**
   * Change the pagination page and fetch the Photo Editing Jobs.
   */
  @action
  changePage(page) {
    this.pagination.currentPage = page;

    return this.loadIndex();
  }

  /**
   * Change the pagination page and fetch the Photo Editing Jobs.
   */
  @action
  search() {
    this.pagination.currentPage = 1;

    return this.loadIndex();
  }

  /**
   * Set default search params.
   */
  @action
  setDefaultSearchParams() {
    this.searchParams.photoEditingJobIdOrLabel = "";
    this.searchParams.providerId = "";
    this.searchParams.tourId = "";
    this.searchParams.status = "";
    this.searchParams.statuses = "";
  }

  @action
  delete(photoEditingJobId) {
    this.isLoading = true;

    return this.photoEditingJobRepository
      .delete(photoEditingJobId)
      .then(() => this.getAll())
      .finally(() => {
        this.isLoading = false;
      });
  }

  @computed
  get costPerItem() {
    return this.photoEditingImageProduct !== null ? this.photoEditingImageProduct.discountedCreditCost : 0;
  }

  @computed
  get totalCost() {
    if (this.photoEditingJob === null) {
      return 0;
    }

    const numberOfProducts =
      this.photoEditingJob.typeId === JobType.HD_REAL
        ? Math.ceil(this.photoEditingJob.numberOfProducts / HdRealImagesPerProduct)
        : this.photoEditingJob.numberOfProducts;

    return this.costPerItem * (numberOfProducts - this.photoEditingJob.numberOfFreeProducts);
  }

  @computed
  get downloadProcessedImagesUrl() {
    return this.photoEditingJobRepository.getDownloadProcessedImagesUrl(this.photoEditingJobId);
  }

  @action
  getPhotoEditingImageProduct(typeId, productId) {
    return this.productRepository.fetchOne(productId).then(response => {
      this.photoEditingImageProducts[typeId] = new Product(response.data.product);
    });
  }

  @action
  togglePhotoEditingImageSelection(photoEditingImageId) {
    const photoEditingImage = this.photoEditingJob.photoEditingImages.find(
      scopedPhotoEditingImage => scopedPhotoEditingImage.photoEditingImageId === photoEditingImageId,
    );

    photoEditingImage.isSelected = !photoEditingImage.isSelected;
  }

  @action
  toggleAllPhotoEditingImagesSelection() {
    const allSelected = this.photoEditingJob.photoEditingImages
      .filter(({ isReady }) => isReady)
      .every(({ isSelected }) => isSelected);

    for (let i = 0; i < this.photoEditingJob.photoEditingImages.length; i += 1) {
      const photoEditingImage = this.photoEditingJob.photoEditingImages[i];
      // only process ready images. Technically an unready image cannot be selected nor unselected.
      if (photoEditingImage.isReady) {
        photoEditingImage.isSelected = !allSelected;
      }
    }
  }

  @action
  refund(photoEditingJobId) {
    this.isLoading = true;

    return this.photoEditingJobRepository
      .refund(photoEditingJobId)
      .then(response => {
        this.photoEditingJob.setRefunds(response.data.photoEditingJob.refunds);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }
}

export default PhotoEditingJobStore;
